import { createClient } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { canUseInternalAdmin } from './internalUsers';
import { shortCouncilName } from '../config/registry';

const publicAuditSupabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      storageKey: 'sitescope-public-firm-audit',
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        'x-sitescope-public-surface': 'firm-audit',
      },
    },
  }
);

export function canUseFirmAuditAdmin(email) {
  return canUseInternalAdmin(email);
}

export function formatAuditMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return 'No cost data';
  if (num >= 1000000) return `$${(num / 1000000).toFixed(num >= 10000000 ? 0 : 1)}M`;
  if (num >= 1000) return `$${Math.round(num / 1000)}K`;
  return `$${Math.round(num)}`;
}

export function shortCouncil(council) {
  return shortCouncilName(council);
}

export function prettyRole(role) {
  const value = String(role || '').trim();
  if (!value) return 'Observed';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function auditUrlForFirm(firmId, origin = window.location.origin) {
  return `${origin}/audit/${encodeURIComponent(firmId)}`;
}

export function auditRefForLead(lead) {
  return lead?.audit_ref || lead?.slug || lead?.id;
}

export function buildSmsText(lead, origin = window.location.origin) {
  const name = lead?.name || 'your business';
  const count = Number(lead?.active_da_count ?? lead?.da_count ?? 0);
  const value = formatAuditMoney(lead?.active_pipeline_value ?? lead?.pipeline_value);
  const url = auditUrlForFirm(auditRefForLead(lead), origin);
  const countLabel = count === 1 ? '1 active DA' : `${count} active DAs`;
  const valueBit = value && value !== 'No cost data' ? ` worth ${value}` : '';
  return `Hey [name], SiteScope found ${countLabel}${valueBit} linked to ${name} across the Northern Rivers. See your footprint: ${url}`;
}

export async function fetchPublicFirmAudit(firmRef) {
  const { data, error } = await publicAuditSupabase.rpc('get_public_firm_audit', {
    p_firm_ref: firmRef,
  });
  if (error) throw error;
  return data;
}

export async function fetchInternalFirmAuditLeads(limit = 100) {
  const { data, error } = await supabase.rpc('get_internal_firm_audit_leads', {
    p_limit: limit,
  });
  if (error) throw error;
  return data?.leads || [];
}

export async function recordFirmAuditEvent(firmId, eventType, metadata = {}) {
  if (!firmId || !eventType) return false;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(firmId))) {
    return false;
  }
  const { error } = await supabase.rpc('record_firm_audit_event', {
    p_firm_id: firmId,
    p_event_type: eventType,
    p_metadata: metadata,
  });
  if (error) {
    console.warn('[firm_audit_event] failed', error.message || error);
    return false;
  }
  return true;
}
